import * as Yup from "yup"
import {
	AssetType,
	PositionType,
} from "models/asset"
import {
	Brand,
	BrandRequisite,
} from "models/brand"
import {
	BrandProfileUpdateRequest,
	CreateRepresentativeRequest,
	RequisiteRequest,
} from "models/brand-profile-update"
import { JobType } from "models/representative"
import { ContractType } from "models/contract"

export class RequisitesForm {
	active: string = "y"
	positionType: PositionType | null = null
	contracts?: string[] = []
	window?: string | null = "n"
	singleFloor?: string | null = "n"
	assetType?: AssetType | null = null
	enableStore: boolean = false
	minGla?: number | null = null
	maxGla?: number | null = null
	enableLand: boolean = false
	minLandArea?: number | null = null
	maxLandArea?: number | null = null

	public static empty(positionType: PositionType | null): RequisitesForm {
		const result = new RequisitesForm()
		result.positionType = positionType
		result.active = "n"

		return result
	}

	constructor(requisites?: BrandRequisite) {
		if (requisites === null || requisites === undefined) {
			return
		}

		this.positionType = requisites.positionType
		this.contracts = Object.entries(requisites.preferredContractTypes)
			.filter(([_, v]) => v === true)
			.map(([k, _]) => k)
		this.window = requisites.shopWindows ? "y" : "n"
		this.singleFloor = requisites.singleLevel ? "y" : "n"
		this.minGla = requisites.minGla
		this.maxGla = requisites.maxGla
		this.minLandArea = requisites.minLandArea
		this.maxLandArea = requisites.maxLandArea

		this.enableStore = this.minGla !== null && this.minGla !== undefined
		this.enableLand = this.minLandArea !== null && this.minLandArea !== undefined

		const preferredType = requisites.preferredAssetType
		const assetTypeValues = Object.values(AssetType) as string[]
		if (preferredType && assetTypeValues.includes(preferredType)) {
			this.assetType = preferredType as AssetType
		} else if (this.enableStore && !this.enableLand) {
			this.assetType = AssetType.WAREHOUSE_OR_STORE
		} else if (this.enableLand && !this.enableStore) {
			this.assetType = AssetType.PLOT_OF_LAND
		} else if (this.enableStore && this.enableLand) {
			this.assetType = AssetType.WAREHOUSE_OR_STORE
		} else {
			this.assetType = null
		}
	}
}

export class UpdateBrandForm {
	brandName?: string | null = null

	representativeId?: string | null = null
	representative?: string | null = null
	email?: string | null = null
	phone?: string | null = null
	job?: JobType | null = null

	company?: string | null = null
	products?: string[] = []

	requisitesGeneric: RequisitesForm = new RequisitesForm()
	requisitesUrban: RequisitesForm | null = RequisitesForm.empty(PositionType.URBAN)
	requisitesSuburban: RequisitesForm | null = RequisitesForm.empty(PositionType.SUBURBAN)
	requisitesHighStreet: RequisitesForm | null = RequisitesForm.empty(PositionType.HIGH_STREET)
	requisitesMall: RequisitesForm | null = RequisitesForm.empty(PositionType.MALL)
	requisitesRetailPark: RequisitesForm | null = RequisitesForm.empty(PositionType.RETAIL_PARK)
	requisitesStandalone: RequisitesForm | null = RequisitesForm.empty(PositionType.STANDALONE)

	public static fromServerResponse(response: Brand): UpdateBrandForm {
		const form = new UpdateBrandForm()
		form.brandName = response.name
		form.company = response.company.id
		form.products = response.products.map(it => it.id)

		if (response.representatives?.length > 0) {
			form.representativeId = response.representatives[0].id
			form.representative = response.representatives[0].name
			form.email = response.representatives[0].email
			form.phone = response.representatives[0].phone
			form.job = response.representatives[0].job
		}

		if (response.requisites)
			for (let requisite of response.requisites.requisites) {
				const requisiteForm = new RequisitesForm(requisite)
				switch (requisite.positionType) {
					case PositionType.URBAN:
						form.requisitesUrban = requisiteForm
						break
					case PositionType.SUBURBAN:
						form.requisitesSuburban = requisiteForm
						break
					case PositionType.HIGH_STREET:
						form.requisitesHighStreet = requisiteForm
						break
					case PositionType.MALL:
						form.requisitesMall = requisiteForm
						break
					case PositionType.RETAIL_PARK:
						form.requisitesRetailPark = requisiteForm
						break
					case PositionType.STANDALONE:
						form.requisitesStandalone = requisiteForm
						break
					default:
						if (requisiteForm.positionType === null) {
							form.requisitesGeneric = requisiteForm
						}
						break
				}
			}

		return form
	}

	public toRequest(): BrandProfileUpdateRequest {
		const result: BrandProfileUpdateRequest = new BrandProfileUpdateRequest()
		result.name = this.brandName
		result.company = this.company
		result.products = this.products

		result.representative = new CreateRepresentativeRequest()
		result.representative.id = this.representativeId
		result.representative.name = this.representative
		result.representative.email = this.email
		result.representative.phone = this.phone
		result.representative.job = this.job

		const generic = this.requisiteToRequest(this.requisitesGeneric)!!
		result.requisites.push(generic)

		const urban = this.requisiteToRequest(this.requisitesUrban)
		if (urban) {
			result.requisites.push(urban)
		}
		const suburban = this.requisiteToRequest(this.requisitesSuburban)
		if (suburban) {
			result.requisites.push(suburban)
		}
		const highStreet = this.requisiteToRequest(this.requisitesHighStreet)
		if (highStreet) {
			result.requisites.push(highStreet)
		}
		const mall = this.requisiteToRequest(this.requisitesMall)
		if (mall) {
			result.requisites.push(mall)
		}
		const retailPark = this.requisiteToRequest(this.requisitesRetailPark)
		if (retailPark) {
			result.requisites.push(retailPark)
		}
		const standalone = this.requisiteToRequest(this.requisitesStandalone)
		if (standalone) {
			result.requisites.push(standalone)
		}

		return result
	}

	public requisiteToRequest(form?: RequisitesForm | null): RequisiteRequest | null {
		if (form === undefined || form === null) {
			return null
		}
		if (form.active === "n") {
			return null
		}

		const request = new RequisiteRequest()
		request.preferredContractTypes = this.mapContractTypeValuesForRequest(form)
		request.preferredAssetType = form.assetType
		if (form.enableStore) {
			request.minGla = form.minGla
			request.maxGla = form.maxGla
		} else {
			request.minGla = null
			request.maxGla = null
		}
		if (form.enableLand) {
			request.minLandArea = form.minLandArea
			request.maxLandArea = form.maxLandArea
		} else {
			request.minLandArea = null
			request.maxLandArea = null
		}
		request.positionType = form.positionType
		request.shopWindows = form.window === "y"
		request.singleLevel = form.singleFloor === "y"

		return request
	}

	private mapContractTypeValuesForRequest(form: RequisitesForm): { [id: string]: boolean } {
		const enumValues = Object.values(ContractType)
		const result: Record<string, boolean> = {}

		enumValues.forEach((enumValue) => {
			if (!form.contracts) {
				return
			}

			result[enumValue] = form.contracts?.includes(enumValue)
		})

		return result
	}
}

function validateOnlyIfActive(active: string, validation: any, schema: any) {
	if (active === "y") {
		return validation
	} else {
		return schema.notRequired().nullable()
	}
}

const RequisiteValidation = {
	contracts: Yup.array().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.min(1).required(), schema)
	),
	window: Yup.string().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.required(), schema)
	),
	singleFloor: Yup.string().when("active", ([active], schema) =>
		validateOnlyIfActive(active, schema.required(), schema)
	),
	assetType: Yup.string().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.required(), schema)
	),
	minGla: Yup.number().when("active",
		([active], schema) =>
			validateOnlyIfActive(
				active,
				schema.positive()
					.transform((v, o) => o === '' ? null : v)
					.notRequired()
					.nullable(),
				schema
			)
	),
	maxGla: Yup.number().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.positive()
				.transform((v, o) => o === '' ? null : v)
				.notRequired()
				.nullable(), schema)
	),
	minLandArea: Yup.number().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.positive()
				.transform((v, o) => o === '' ? null : v)
				.notRequired()
				.nullable(), schema)
	),
	maxLandArea: Yup.number().when("active",
		([active], schema) =>
			validateOnlyIfActive(active, schema.positive()
				.transform((v, o) => o === '' ? null : v)
				.notRequired()
				.nullable(), schema)
	),
}

const BrandValidation = {
	brandName: Yup.string().max(256).required(),

	representative: Yup.string().max(256).required(),
	email: Yup.string().email().max(254).required(),
	phone: Yup.string().matches(/^[0-9]*$/).max(20).required(),
	job: Yup.string().required(),

	company: Yup.string().required(),
	products: Yup.array()
		.notRequired()
		.nullable(),

	requisitesGeneric: Yup.object(RequisiteValidation).required(),
	requisitesUrban: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
	requisitesSuburban: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
	requisitesHighStreet: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
	requisitesMall: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
	requisitesRetailPark: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
	requisitesStandalone: Yup.object(RequisiteValidation)
		.notRequired()
		.nullable(),
}

export default Yup.object()
	.shape(
		BrandValidation,
	)
	.required()
